import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { ColDef, GridOptions } from 'ag-grid-community';
import { finalize } from 'rxjs';

import { TranslateModule } from '@ngx-translate/core';
import { CommonAgGridComponent } from '../common-ag-grid/common-ag-grid.component';
import { GridLoadingOverlayComponent } from '../grid-loading-overlay/grid-loading-overlay.component';
import { DataListDialogData } from '../models/data-list-dialog.models';

@Component({
  selector: 'lib-data-list-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    TranslateModule,
    CommonAgGridComponent,
  ],
  templateUrl: './data-list-dialog.component.html',
  styleUrls: ['./data-list-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataListDialogComponent<T> {
  private readonly dialogRef = inject(MatDialogRef<DataListDialogComponent<T>>);
  private readonly data = inject<DataListDialogData<T>>(MAT_DIALOG_DATA);
  readonly title = signal(this.data.title);
  readonly rowData = signal<T[]>([]);
  readonly isLoading = signal(false);
  readonly totalRecords = computed(() => this.rowData().length);
  readonly colDefs = signal(this.data.columns);

  readonly defaultColDef: ColDef<T> = {
    sortable: true,
    resizable: true,
    suppressHeaderMenuButton: true,
    suppressHeaderFilterButton: true,
    filterParams: {
      buttons: [],
    },
  };

  readonly gridOptions: GridOptions<T> = {
    rowHeight: 40,
    loadingOverlayComponent: GridLoadingOverlayComponent,
  };

  constructor() {
    this.loadRows();
  }

  private loadRows(): void {
    this.isLoading.set(true);

    this.data
      .loadData()
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (rows) => this.rowData.set(rows),
        error: () => {
          this.rowData.set([]); // to prevent unhandled promise rejection
        },
      });
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
